import * as React from "react";
import { CardsManagerCallbacks } from "../CardsManager/CardsManager";
import { useSelector } from "react-redux";
import { getCardsByDateNew } from "../../store/calendar/selectors";
import CalendarHeader from "../Calendar/Header/Header";
import { getMonths, groupEmptyMonths } from "./utils";
import Month from "./Month/Month";
import "./MobileCalendar.css";
import { parse, startOfMonth, isSameMonth, format, addYears, startOfYear } from "date-fns";
import { localedFormat } from "../../utils";
import { ca } from "date-fns/locale";
import WelcomeCalendar from "../Calendar/Welcome/WelcomeCalendar";
import ArrowBack from '@material-ui/icons/ArrowBack';

interface Props {
	callbacks: CardsManagerCallbacks;
	year?: string;
	month?: string;
	id?: string;
	openDate: Date;
	setDate: (date: Date) => void;
}

const MobileCalendar: React.FC<Props> = (props) => {
	const defaultDate =
		props.year && props.month
			? parse(
					`${parseInt(props.year)}-${parseInt(props.month) + 1}`,
					"yyyy-M",
					new Date()
			  )
			: new Date();

	const [openDate, setOpenDate] = React.useState(defaultDate);
	const books = useSelector(getCardsByDateNew);
	const monthWrapperRef = React.useRef<HTMLDivElement>(null);
	const calendarRef = React.useRef<HTMLDivElement>(null);

	const highlightedCardId = props.id;

	React.useEffect(() => {
		if (highlightedCardId && calendarRef.current) {
			setTimeout(() => {
				const div = calendarRef.current.querySelector(
					`#month-${defaultDate.getMonth()} div[data-card-id="${highlightedCardId}"]`
				);

				if (div) {
					window.scrollTo({
						top: (div as any).offsetTop - 5,
						behavior: "smooth",
					});
				}
			}, 300);
		}
	}, [highlightedCardId, calendarRef, props.callbacks.cards]);

	const months = React.useMemo(() => {
		return getMonths(
			books[openDate.getFullYear()] ? books[openDate.getFullYear()] : [],
			openDate
		);
	}, [openDate.getFullYear(), books]);

	const changeFocusedMonth = (month: Date) => {
		setOpenDate(startOfMonth(month));
	};

	const focusMonth = (date: Date) => {
		const divAnchor = document.getElementById(`month-${date.getMonth()}`);

		if (divAnchor) {
			window.scrollTo({
				top: (divAnchor as any).offsetTop + 5
			});
		}
	};

	React.useEffect(() => {
		if (!highlightedCardId) {
			focusMonth(openDate ? openDate : new Date());
		}
	}, [openDate.getFullYear()]);

	React.useEffect(() => {
		setOpenDate(props.openDate);
	}, [props.openDate]);

	return (
		<div className="mobile-calendar" ref={calendarRef}>
			<CalendarHeader
				date={props.openDate}
				changeDate={props.setDate}
				headerText={localedFormat(openDate, "LLLL, yyyy")}
				isLoading={props.callbacks.loading}
			/>

			<WelcomeCalendar />

			<div className="mobile-calendar-months" ref={monthWrapperRef}>
				<div
					className="mobile-calendar-prev"
					onClick={() => props.setDate(startOfYear(addYears(openDate, -1)))}
				>
					<ArrowBack />
					{addYears(openDate, -1).getFullYear()}
				</div>

				{months.map((month, key) => (
					<Month
						month={month}
						key={key}
						changeFocusedMonth={changeFocusedMonth}
						focused={openDate.getMonth() === month.month.getMonth()}
					/>
				))}

				<div
					className="mobile-calendar-prev mobile-calendar-prev_next"
					onClick={() => props.setDate(startOfYear(addYears(openDate, 1)))}
				>
					<ArrowBack />
					{addYears(openDate, 1).getFullYear()}
				</div>
			</div>
		</div>
	);
};

export default MobileCalendar;
